import { useState } from "react";
import { ServerSideParams, ServerSideResponse } from "../types";

export function useServerSideTableData<T>(
  apiFunction: (params: ServerSideParams) => Promise<ServerSideResponse<T>>
) {
  const [serverData, setServerData] = useState<T[]>([]);
  const [meta, setMeta] = useState<ServerSideResponse<T>["meta"] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrappedApiFunction = async (
    params: ServerSideParams,
    identifier?: boolean
  ): Promise<ServerSideResponse<T>> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFunction(params);

      if (response.success && !identifier) {
        setServerData(response.data);
        setMeta(response.meta);
      } else if (!response.success) {
        setError(response.message);
        setServerData([]);
        setMeta(null);
      }

      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      setServerData([]);
      setMeta(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const serverSideConfig = {
    enabled: true,
    onDataRequest: wrappedApiFunction,
    debounceMs: 500,
  };

  return {
    // Table configuration
    serverSideConfig,

    // Accessible data
    serverData,
    meta,
    loading,
    error,

    // Computed values
    totalItems: meta?.total || 0,
    currentPageItems: serverData.length,
    totalPages: meta?.totalPages || 0,
    currentPage: meta?.page || 1,
  };
}
